// Authentik OIDC — Authorization Code + PKCE (public client, kein Secret).
// Das Access-Token (JWT) landet in localStorage.token → der $http-Wrapper (mixins.js)
// schickt es als Bearer an das Gateway. cnum-Claim → userdata.id (Frontend-Kompat).
const BASE = process.env.VUE_APP_OIDC_BASE;
const CLIENT_ID = process.env.VUE_APP_OIDC_CLIENT_ID || 'partner-api';
const SCOPE = process.env.VUE_APP_OIDC_SCOPE || 'openid email profile cnum';
const AUTHORIZE = `${BASE}/application/o/authorize/`;
const TOKEN = `${BASE}/application/o/token/`;
// end-session liegt unter dem App-Slug (= client_id in unserem Setup)
const END_SESSION = `${BASE}/application/o/${CLIENT_ID}/end-session/`;

function redirectUri() {
  return `${window.location.origin}/`;
}

function b64url(bytes) {
  let s = btoa(String.fromCharCode.apply(null, new Uint8Array(bytes)));
  return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randomString(len) {
  const a = new Uint8Array(len);
  crypto.getRandomValues(a);
  return b64url(a).slice(0, len);
}
async function sha256(str) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
}

export function parseJwt(token) {
  try {
    let p = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    p += '='.repeat((4 - (p.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(p)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

export function getToken() {
  return localStorage.getItem('token');
}

export function isAuthenticated() {
  const t = getToken();
  if (!t) return false;
  const p = parseJwt(t);
  if (!p || !p.exp) return false;
  return p.exp * 1000 > Date.now() + 5000; // 5s Puffer gegen Clock-Skew
}

export function getUserData() {
  const p = parseJwt(getToken());
  if (!p) return null;
  return {
    id: p.cnum, // = R2_Accounts.id (Match-Kriterium, von der App als cnum genutzt)
    username: p.preferred_username || p.email,
    name: p.name,
    groups: p.groups || [],
  };
}

export async function login() {
  const verifier = randomString(64);
  const challenge = b64url(await sha256(verifier));
  const state = randomString(32);
  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oauth_state', state);
  const url =
    `${AUTHORIZE}?response_type=code&client_id=${encodeURIComponent(CLIENT_ID)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri())}` +
    `&scope=${encodeURIComponent(SCOPE)}` +
    `&state=${encodeURIComponent(state)}` +
    `&code_challenge=${challenge}&code_challenge_method=S256`;
  window.location.assign(url);
}

// true = ein Callback (?code) wurde verarbeitet; wirft bei Fehler
export async function handleRedirectCallback() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code) return false;
  const savedState = sessionStorage.getItem('oauth_state');
  const verifier = sessionStorage.getItem('pkce_verifier');
  const clean = () =>
    window.history.replaceState({}, document.title, redirectUri());
  if (!state || state !== savedState || !verifier) {
    clean();
    throw new Error('OIDC-State/Verifier ungültig');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: CLIENT_ID,
    code_verifier: verifier,
  });
  const res = await fetch(TOKEN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oauth_state');
  if (!res.ok) {
    clean();
    throw new Error('Token-Exchange fehlgeschlagen: ' + res.status);
  }
  const tok = await res.json();
  localStorage.setItem('token', tok.access_token);
  if (tok.refresh_token) localStorage.setItem('refresh_token', tok.refresh_token);
  if (tok.id_token) localStorage.setItem('id_token', tok.id_token);   // für logout-Hint
  clean();
  return true;
}

// Nur lokale Tokens löschen, KEIN Redirect (für den Router-Guard bei abgelaufener Session).
export function clearTokens() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('id_token');
}

// Echtes RP-Logout (nur für den Ausloggen-Button): beendet die Authentik-Session
// via end-session und redirectet. NICHT im Router-Guard aufrufen (sonst Redirect-Loop)!
export function logout() {
  const idToken = localStorage.getItem('id_token');
  clearTokens();
  const url =
    `${END_SESSION}?post_logout_redirect_uri=${encodeURIComponent(window.location.origin + '/')}` +
    (idToken ? `&id_token_hint=${idToken}` : '');
  window.location.assign(url);
}

export default {
  login,
  handleRedirectCallback,
  isAuthenticated,
  getToken,
  getUserData,
  parseJwt,
  clearTokens,
  logout,
};
